import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FiUpload, FiDownload, FiFile, FiEye, FiCheck, FiFilter, FiFileText, FiRefreshCw } from 'react-icons/fi';
import { toast } from 'react-toastify';
import apiClient from '../../api/apiClient';

interface IncomingInvoice {
  id: string;
  invoiceNumber: string;
  invoiceType: string;
  payerName: string;
  supplierName: string;
  marketplace: string;
  periodStart: string | null;
  periodEnd: string | null;
  totalAmount: number | null;
  currency: string | null;
  status: string;
  sapDocumentNumber: string | null;
  hasPdf: boolean;
  source: string | null;
  createdAt: string;
}

interface ImportResult {
  totalRows: number;
  importedCount: number;
  skippedDuplicates: number;
  failedCount: number;
  errors: string[];
  batchId: string;
}

const api = {
  list: (params: any) => apiClient.get('/amazon-incoming-invoices', { params }),
  importExcel: (formData: FormData) =>
    apiClient.post('/amazon-incoming-invoices/import-excel', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadPdfs: (formData: FormData) =>
    apiClient.post('/amazon-incoming-invoices/upload-pdfs', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getPdf: (id: string) => apiClient.get(`/amazon-incoming-invoices/${id}/pdf`, { responseType: 'blob' }),
  updateStatus: (id: string, data: any) => apiClient.put(`/amazon-incoming-invoices/${id}/status`, data),
  exportSap: (params?: any) => apiClient.get('/amazon-incoming-invoices/export-sap', { params, responseType: 'blob' }),
  bulkDownload: (invoiceIds: string[]) =>
    apiClient.post('/amazon-incoming-invoices/bulk-download', { invoiceIds }, { responseType: 'blob' }),
};

const statusColors: Record<string, string> = {
  New: 'badge-draft',
  Reviewed: 'badge-pending',
  ExportedToSap: 'badge-confirmed',
  Archived: 'badge-delivered',
};

const AmazonIncomingInvoicesPage: React.FC = () => {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState<IncomingInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterMarketplace, setFilterMarketplace] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [uploadingPdfs, setUploadingPdfs] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const excelInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const pageSize = 25;

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, pageSize, search };
      if (filterType) params.invoiceType = filterType;
      if (filterMarketplace) params.marketplace = filterMarketplace;
      if (filterStatus) params.status = filterStatus;
      const response = await api.list(params);
      setInvoices(response.data.items);
      setTotal(response.data.totalCount);
    } catch {
      toast.error(t('amazonInvoices.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [page, search, filterType, filterMarketplace, filterStatus, t]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleImportExcel = useCallback(async () => {
    const file = excelInputRef.current?.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.importExcel(formData);
      setImportResult(response.data);
      toast.success(t('amazonInvoices.importedCount', { count: response.data.importedCount }));
      if (excelInputRef.current) excelInputRef.current.value = '';
      fetchInvoices();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('amazonInvoices.importError'));
    } finally {
      setImporting(false);
    }
  }, [fetchInvoices, t]);

  const handleUploadPdfs = useCallback(async () => {
    const files = pdfInputRef.current?.files;
    if (!files?.length) return;
    setUploadingPdfs(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach(f => formData.append('files', f));
      const response = await api.uploadPdfs(formData);
      toast.success(t('amazonInvoices.uploadedPdfs', { count: response.data.importedCount }));
      if (pdfInputRef.current) pdfInputRef.current.value = '';
      fetchInvoices();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('amazonInvoices.uploadError'));
    } finally {
      setUploadingPdfs(false);
    }
  }, [fetchInvoices, t]);

  const handleViewPdf = useCallback(async (id: string) => {
    try {
      const response = await api.getPdf(id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch {
      toast.error(t('amazonInvoices.pdfUnavailable'));
    }
  }, [t]);

  const handleExportSap = useCallback(async () => {
    try {
      const response = await api.exportSap();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `amazon_invoices_sap_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('amazonInvoices.sapExportSuccess'));
      fetchInvoices();
    } catch {
      toast.error(t('amazonInvoices.exportError'));
    }
  }, [fetchInvoices, t]);

  const handleBulkDownload = useCallback(async () => {
    if (selected.size === 0) return;
    try {
      const response = await api.bulkDownload(Array.from(selected));
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `amazon_faktury_${new Date().toISOString().slice(0,10)}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('amazonInvoices.downloadedPdfs', { count: selected.size }));
    } catch {
      toast.error(t('amazonInvoices.downloadError'));
    }
  }, [selected, t]);

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === invoices.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(invoices.map(i => i.id)));
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="page-container page-container--wide">
      <div className="page-header">
        <h1>{t('amazonInvoices.title')}</h1>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={fetchInvoices} title={t('common.refresh')}>
            <FiRefreshCw />
          </button>
          <button className="btn btn-primary" onClick={handleExportSap}>
            <FiDownload /> {t('amazonInvoices.exportToSap')}
          </button>
        </div>
      </div>

      {/* Import section */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-body" style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '250px' }}>
            <label style={{ fontWeight: 600, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
              {t('amazonInvoices.importExcelLabel')}
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input ref={excelInputRef} type="file" accept=".xlsx,.xls" className="form-control" style={{ flex: 1 }} />
              <button className="btn btn-primary" onClick={handleImportExcel} disabled={importing}>
                <FiUpload /> {importing ? t('amazonInvoices.importing') : t('amazonInvoices.importExcel')}
              </button>
            </div>
          </div>
          <div style={{ flex: 1, minWidth: '250px' }}>
            <label style={{ fontWeight: 600, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
              {t('amazonInvoices.uploadPdfLabel')}
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input ref={pdfInputRef} type="file" accept=".pdf" multiple className="form-control" style={{ flex: 1 }} />
              <button className="btn btn-secondary" onClick={handleUploadPdfs} disabled={uploadingPdfs}>
                <FiUpload /> {uploadingPdfs ? t('amazonInvoices.uploading') : t('amazonInvoices.uploadPdfs')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Import result */}
      {importResult && (
        <div className="bulk-actions-bar" style={{ marginBottom: '1rem', background: 'var(--success-light)', borderColor: 'var(--success)' }}>
          <span>
            {t('amazonInvoices.importResultLabel')}: <strong>{importResult.importedCount}</strong> {t('amazonInvoices.importNew')},{' '}
            <strong>{importResult.skippedDuplicates}</strong> {t('amazonInvoices.importDuplicatesSkipped')},{' '}
            <strong>{importResult.failedCount}</strong> {t('amazonInvoices.importErrors')}
          </span>
          {importResult.errors.length > 0 && (
            <details style={{ marginTop: '0.5rem' }}>
              <summary style={{ cursor: 'pointer', color: 'var(--danger)' }}>{t('amazonInvoices.showErrors')}</summary>
              {importResult.errors.map((e, i) => <div key={i} style={{ fontSize: '12px' }}>{e}</div>)}
            </details>
          )}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="form-control"
          placeholder={t('amazonInvoices.searchPlaceholder')}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          style={{ maxWidth: '300px' }}
        />
        <select className="form-control" value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }} style={{ maxWidth: '160px' }}>
          <option value="">{t('common.allStatuses')}</option>
          <option value="New">{t('amazonInvoices.statusNew')}</option>
          <option value="Reviewed">{t('amazonInvoices.statusReviewed')}</option>
          <option value="ExportedToSap">{t('amazonInvoices.statusInSap')}</option>
          <option value="Archived">{t('amazonInvoices.statusArchived')}</option>
        </select>
        <select className="form-control" value={filterMarketplace} onChange={(e) => { setFilterMarketplace(e.target.value); setPage(1); }} style={{ maxWidth: '160px' }}>
          <option value="">{t('amazonInvoices.allMarketplaces')}</option>
          {['Amazon.de','Amazon.pl','Amazon.it','Amazon.fr','Amazon.es','Amazon.co.uk','Amazon.nl','Amazon.se','Amazon.com.be','Amazon.ie'].map(m =>
            <option key={m} value={m}>{m}</option>
          )}
        </select>
        <select className="form-control" value={filterType} onChange={(e) => { setFilterType(e.target.value); setPage(1); }} style={{ maxWidth: '250px' }}>
          <option value="">{t('common.allTypes')}</option>
          <option value="Fulfillment by Amazon Tax Invoice">FBA Tax Invoice</option>
          <option value="Merchant VAT Invoice">Merchant VAT Invoice</option>
          <option value="Merchant VAT Credit Note">Merchant VAT Credit Note</option>
          <option value="Amazon Marketing Service VAT Invoice">Marketing Invoice</option>
          <option value="Merchant Buy Shipping Label">Shipping Label</option>
        </select>
        {selected.size > 0 && (
          <button className="btn btn-secondary" onClick={handleBulkDownload}>
            <FiDownload /> {t('amazonInvoices.downloadPdfs', { count: selected.size })}
          </button>
        )}
      </div>

      {/* Table */}
      <div className="card">
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <input type="checkbox" checked={selected.size === invoices.length && invoices.length > 0} onChange={toggleSelectAll} />
                </th>
                <th>{t('invoices.invoiceNumber')}</th>
                <th>{t('invoices.type')}</th>
                <th>{t('amazonInvoices.supplier')}</th>
                <th>{t('amazonInvoices.marketplace')}</th>
                <th>{t('amazonInvoices.period')}</th>
                <th>{t('invoices.status')}</th>
                <th>SAP</th>
                <th>{t('common.pdf')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: '2rem' }}><div className="loader" /></td></tr>
              ) : invoices.length === 0 ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: '2rem' }}>{t('common.noData')}</td></tr>
              ) : invoices.map(inv => (
                <tr key={inv.id}>
                  <td><input type="checkbox" checked={selected.has(inv.id)} onChange={() => toggleSelect(inv.id)} /></td>
                  <td style={{ fontWeight: 600, fontSize: '12px' }}>{inv.invoiceNumber}</td>
                  <td style={{ fontSize: '11px' }}>{inv.invoiceType.replace('Fulfillment by Amazon ', 'FBA ').replace('Amazon Marketing Service ', 'AMS ')}</td>
                  <td style={{ fontSize: '11px' }}>{inv.supplierName}</td>
                  <td><span className="badge badge-info" style={{ fontSize: '10px' }}>{inv.marketplace}</span></td>
                  <td style={{ fontSize: '11px' }}>
                    {inv.periodStart ? new Date(inv.periodStart).toLocaleDateString('pl') : '-'}
                    {inv.periodEnd ? ` — ${new Date(inv.periodEnd).toLocaleDateString('pl')}` : ''}
                  </td>
                  <td><span className={`badge ${statusColors[inv.status] || 'badge-draft'}`}>{inv.status}</span></td>
                  <td style={{ fontSize: '11px' }}>{inv.sapDocumentNumber || '-'}</td>
                  <td>{inv.hasPdf ? <FiFileText style={{ color: 'var(--success)' }} /> : <FiFile style={{ color: 'var(--text-secondary)' }} />}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      {inv.hasPdf && (
                        <button className="btn btn-sm btn-secondary" onClick={() => handleViewPdf(inv.id)} title={t('amazonInvoices.previewPdf')}>
                          <FiEye />
                        </button>
                      )}
                      {inv.status === 'New' && (
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={async () => {
                            await api.updateStatus(inv.id, { status: 'Reviewed' });
                            fetchInvoices();
                          }}
                          title={t('amazonInvoices.markAsReviewed')}
                        >
                          <FiCheck />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
          <button className="btn btn-sm btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>{t('amazonInvoices.previous')}</button>
          <span style={{ padding: '0.5rem', fontSize: '13px' }}>{t('amazonInvoices.pageInfo', { page, totalPages, total })}</span>
          <button className="btn btn-sm btn-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>{t('amazonInvoices.next')}</button>
        </div>
      )}
    </div>
  );
};

export default AmazonIncomingInvoicesPage;
